import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  can as canPure,
  getMyPermissions,
  type PermissionAction,
  type PermissionModule,
  type UserPermissionMatrix,
} from '../lib/permissionsApi';
import { useAuthStore } from '../store/useAuthStore';
import { useModules } from './useModules';
import { isFlagMasked } from '../lib/moduleCatalog';

export interface UsePermissionsResult {
  /** The resolved matrix, or undefined while loading / when unauthenticated. */
  matrix: UserPermissionMatrix | undefined;
  /** True until the matrix has loaded once — gate UI on this to avoid flicker. */
  isLoading: boolean;
  isError: boolean;
  /** Deny-by-default flag check, mirroring the backend `can()`. */
  can: (module: PermissionModule, action: PermissionAction) => boolean;
}

/**
 * usePermissions — the frontend RBAC entry point (CLE-216). Loads the caller's
 * full matrix from `GET /api/me/permissions` once and caches it via React Query
 * (keyed by user id so it refreshes on login/logout). Components call
 * `can(module, action)` to gate sidebar sections, buttons, cards, AI and
 * Settings. Render `isLoading` as a skeleton so nothing flickers before the
 * matrix arrives.
 */
export function usePermissions(): UsePermissionsResult {
  const userId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.role);
  const isAuthenticated = Boolean(userId);
  // ADMIN is an unrestricted super-role: it must always see every action,
  // regardless of the stored matrix or whether it has finished loading. This
  // mirrors the backend `Admin` template (full access) and guards against a
  // missing/empty UserPermission row hiding create buttons from admins.
  const isAdmin = role === 'ADMIN';

  const query = useQuery({
    queryKey: ['permissions', 'me', userId],
    queryFn: getMyPermissions,
    enabled: isAuthenticated,
    // An admin editing a role template re-syncs every assigned account on the
    // backend (CLE-431), but that admin's browser can't push a cache-bust to the
    // affected users' sessions. So the affected user's own session must pick the
    // change up on its own: keep the matrix fresh for only a short window,
    // refetch when they return to the tab or reconnect, and poll on a slow
    // interval so a live session converges without a manual hard refresh.
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const matrix = query.data;

  // A module that is off (Firmio Console ceiling or Settings → Module) is
  // hidden from admins too, flag by flag for sub-modules (the backend already
  // masks it in everyone else's matrix).
  const { off } = useModules();

  const can = useCallback(
    (module: PermissionModule, action: PermissionAction) =>
      (isAdmin && !isFlagMasked(off, module, action)) || canPure(matrix, module, action),
    [matrix, isAdmin, off],
  );

  return {
    matrix,
    // Admins never gate on the matrix, so they are never "loading" for UI purposes.
    isLoading: !isAdmin && isAuthenticated && query.isLoading,
    isError: query.isError,
    can,
  };
}
